import React, {useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import { InputComponent } from "../../../components/Input/InputComponent";
import { FA, MD, AI, RI, BS } from "../../../common/Icons";
import { PrimaryButton } from "../../../components/Button/PrimaryButton/PrimaryButton";
import { signUpValidations } from "../../../validation";
import WebRoutes from "../../WebRoutes";
import apiRoutes from "../../../api/ApiRoutes";
import ApiCall from "../../../api/network";
import { toast } from "react-hot-toast";
import { redirectToOrganizationDomain } from "../../../hooks/saveTokenDomain";
import Select from "react-select";
import OrganizationCodeModal from "../../../components/OrganizationCodeModal";

// ------------------ CONFIG ------------------
const iconColor = "gray";
const iconSize = "18px";
const firstNameRightIconSize = "14px";

const initialStateForm = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    organizationCode: "",
    newLetterSubscribe: true,
    acceptPolicy: true,
    province: "",
    taxRate: 0,
    taxType: "",
    taxRegionUuid: "",
};

const buildRegionOptions = (regions = {}) => {
    return Object.keys(regions).map((country) => ({
        label: `${country}:`,
        options: regions[country].map((item) => ({
            label: `${item.region}`,
            value: item.tax_region_uuid,
            region: item.region,
            taxRate: Number(item.tax_percentage),
            taxType: item.tax_type,
            country: item.country,
        })),
    }));
};

// ------------------ COMPONENT ------------------
export const SignUpForm = () => {
    const [formInput, setFormInputs] = useState(initialStateForm);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [submitCalled, setSubmitCalled] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [regionsLoading, setRegionsLoading] = useState(false);
    const navigate = useNavigate();
    const [regionOptions, setRegionOptions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [showOrgCodeModal, setShowOrgCodeModal] = useState(false);

    const fecthRegions = async () => {
        setRegionsLoading(true);
        try {
            const resp = await ApiCall.any(apiRoutes.fetchTaxRegions);
            if (resp?.data) {
                setRegionOptions(buildRegionOptions(resp.data));
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setRegionsLoading(false);
        }
    };

    useEffect(() => {
        fecthRegions();
    }, []);

    const selectStyles = {
        control: (base) => ({
            ...base,
            border: 0,
            boxShadow: "none",
        }),
        groupHeading: (base) => ({
            ...base,
            fontSize: "15px",
            fontWeight: "700",
            color: "#000",
            padding: "8px 12px",
            backgroundColor: "#e9ecef",
            textTransform: "uppercase",
        }),
        option: (provided, state) => ({
            ...provided,
            fontWeight: "normal",
            color: "black",
            border: "none",
            height: "44px",
            boxShadow: "none",
            backgroundColor: state.isSelected
                ? "#F25926"
                : state.isFocused
                    ? "#f57e56"
                    : "transparent",
            ":active": {
                backgroundColor: "#F25926",
            },
        }),
        singleValue: (provided) => ({
            ...provided,
            color: "black",
            fontWeight: "400",
        }),
    };

    // ---------- Handlers ----------
    const onChangeHandler = (event) => {
        const { name, value } = event.target;
        const updated = { ...formInput, [name]: value };
        setFormInputs(updated);
        if (submitCalled) {
            setValidations(updated);
        }
    };

    const togglePassword = () => setShowPassword(!showPassword);
    const toggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    const setValidations = (form) => {
        const errors = signUpValidations(form);
        setErrors(errors);
    };

    const signUpHandler = () => {
        const validationErrors = signUpValidations(formInput);
        setErrors(validationErrors);
        setSubmitCalled(true);
        if (Object.keys(validationErrors).length > 0) return;

        if (!formInput.acceptPolicy) {
            toast.dismiss();
            toast.error("Please accept the Privacy Policy & Terms.");
            return;
        }

        // A code typed into the field above is enough - the popup only steps in
        // to ask when the member left it blank.
        const organizationCode = formInput.organizationCode.trim();
        if (organizationCode.length > 0) {
            registerMember(organizationCode);
            return;
        }

        setShowOrgCodeModal(true);
    };

    const registerMember = async (organizationCode) => {
        setLoading(true);
        let formDataApi = new FormData();
        formDataApi.append("first_name", formInput.firstName);
        formDataApi.append("last_name", formInput.lastName);
        formDataApi.append("email", formInput.email);
        formDataApi.append("password", formInput.password);
        formDataApi.append("password_confirmation", formInput.confirmPassword);
        formDataApi.append("tax_region", formInput.province);
        formDataApi.append("tax_region_uuid", formInput.taxRegionUuid);
        formDataApi.append("tax", formInput.taxRate);
        formDataApi.append("tax_type", formInput.taxType);
        formDataApi.append("code", organizationCode?.length > 0 ? organizationCode : "4c66");
        formDataApi.append("is_subscribed", formInput.newLetterSubscribe ? 1 : 0);

        const response = await ApiCall.any(apiRoutes.signUpMember, {}, formDataApi);

        if (response?.status === 1) {
            setShowOrgCodeModal(false);
            if (response?.data?.email_verified) {
                localStorage.setItem("token", response.meta.token);
                localStorage.setItem("responseUser", JSON.stringify(response));
                toast.success(response?.message);
                redirectToOrganizationDomain(response);
            } else {
                // No session until the email is verified - hand off to the login
                // page, which shows the "Verify Your Email" card with the resend link.
                localStorage.removeItem("token");
                localStorage.removeItem("responseUser");
                setFormInputs(initialStateForm);
                setSelectedRegion(null);
                setSubmitCalled(false);
                toast.success("Please check your email to verify your account");
                navigate(WebRoutes.signin, { state: { unverifiedUser: response } });
            }
        } else if (response !== -1) {
            // -1 means network.js already toasted the API's own error (e.g. "The
            // email has already been taken.") - no need to stack a generic one.
            toast.error(response?.message || "Something went wrong, Try Again Later");
        }

        setLoading(false);
    };

    // ---------- RENDER ----------
    return (
        <>
            <InputComponent
                placeHolder="First Name"
                type="text"
                name="firstName"
                className="width-100"
                onChange={onChangeHandler}
                value={formInput.firstName}
                iconLeft={<FA.FaUser color={iconColor} size={iconSize} />}
                iconRight={
                    <BS.BsFillChatSquareDotsFill
                        color={iconColor}
                        size={firstNameRightIconSize}
                    />
                }
                error={errors?.firstName}
            />

            <InputComponent
                placeHolder="Last Name"
                type="text"
                name="lastName"
                className="width-100"
                onChange={onChangeHandler}
                value={formInput.lastName}
                iconLeft={<FA.FaUser color={iconColor} size={iconSize} />}
                error={errors?.lastName}
            />

            <InputComponent
                placeHolder="Email"
                type="email"
                name="email"
                onChange={onChangeHandler}
                className="width-100"
                value={formInput.email}
                iconLeft={<MD.MdEmail color={iconColor} size={iconSize} />}
                error={errors?.email}
            />

            <div className="mt-3 mb-3">
                <Select
                    placeholder={regionsLoading ? "Loading Province..." : "Select Province / State"}
                    isLoading={regionsLoading}
                    isDisabled={regionsLoading}
                    options={regionOptions}
                    value={selectedRegion}
                    onChange={(option) => {
                        setSelectedRegion(option);
                        setFormInputs({
                            ...formInput,
                            province: option.region,
                            taxRegionUuid: option.value,
                            taxRate: option.taxRate,
                            taxType: option.taxType,
                        });
                    }}
                    isClearable
                    styles={selectStyles}
                />
                {errors?.province && (
                    <small className="text-danger">{errors.province}</small>
                )}
            </div>

            <InputComponent
                placeHolder="Password"
                type={!showPassword ? "password" : "text"}
                name="password"
                value={formInput.password}
                onChange={onChangeHandler}
                iconLeft={<AI.AiFillLock color={iconColor} size={iconSize} />}
                className="width-100"
                iconRight={
                    <div className="d-flex me-1">
                        <RI.RiRotateLockFill
                            color={iconColor}
                            className="mx-1 icon-unselectable"
                            size={iconSize}
                        />
                        {!showPassword ? (
                            <AI.AiFillEye
                                color={iconColor}
                                className="mx-1 icon-unselectable"
                                size={iconSize}
                                onClick={togglePassword}
                            />
                        ) : (
                            <AI.AiFillEyeInvisible
                                color={iconColor}
                                className="mx-1 icon-unselectable "
                                size={iconSize}
                                onClick={togglePassword}
                            />
                        )}
                    </div>
                }
                error={errors?.password}
            />

            <InputComponent
                placeHolder="Confirm Password"
                type={!showConfirmPassword ? "password" : "text"}
                name="confirmPassword"
                value={formInput.confirmPassword}
                onChange={onChangeHandler}
                iconLeft={<AI.AiFillLock color={iconColor} size={iconSize} />}
                className="width-100"
                iconRight={
                    <div className="d-flex me-1">
                        <RI.RiRotateLockFill
                            color={iconColor}
                            className="mx-1 icon-unselectable"
                            size={iconSize}
                        />
                        {!showConfirmPassword ? (
                            <AI.AiFillEye
                                color={iconColor}
                                className="mx-1 icon-unselectable"
                                size={iconSize}
                                onClick={toggleConfirmPassword}
                            />
                        ) : (
                            <AI.AiFillEyeInvisible
                                color={iconColor}
                                className="mx-1 icon-unselectable "
                                size={iconSize}
                                onClick={toggleConfirmPassword}
                            />
                        )}
                    </div>
                }
                error={errors?.confirmPassword}
            />

            <p className="mt-3 mb-1 registered-text text-white">
                Belong to an organization that has a Trip Merchant Partnership?
                If so, enter the correct organization code below. If unknown,
                contact your organization to acquire the code.
            </p>

            <InputComponent
                placeHolder="Organization Code"
                type="text"
                name="organizationCode"
                className="width-100"
                onChange={onChangeHandler}
                value={formInput.organizationCode}
                iconLeft={<MD.MdOutlineHomeWork color={iconColor} size={iconSize} />}
                error={errors?.organizationCode}
            />

            <div className="form-check">
                <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formInput.newLetterSubscribe}
                    id="newsletter"
                    onChange={() =>
                        setFormInputs({
                            ...formInput,
                            newLetterSubscribe: !formInput.newLetterSubscribe,
                        })
                    }
                />
                <label className="form-check-label" htmlFor="newsletter">
                    Subscribe To Newsletter
                </label>
            </div>

            <div className="form-check">
                <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formInput.acceptPolicy}
                    id="policy"
                    onChange={() =>
                        setFormInputs({
                            ...formInput,
                            acceptPolicy: !formInput.acceptPolicy,
                        })
                    }
                />
                <label className="form-check-label" htmlFor="policy">
                    By registering, you agree to our{" "}
                    <Link
                        to={WebRoutes.privacy}
                        className="cursor-pointer hover-text text-decoration fwt-bold"
                    >
                        privacy policy
                    </Link>{" "}
                    and{" "}
                    <Link
                        to={WebRoutes.terms}
                        className="cursor-pointer hover-text text-decoration fwt-bold"
                    >
                        terms and conditions
                    </Link>
                    .
                </label>
            </div>

            {errors?.acceptPolicy && (
                <small className="text-danger">{errors.acceptPolicy}</small>
            )}

            <PrimaryButton
                loading={loading}
                title="REGISTER NOW"
                onClick={signUpHandler}
            />

            <p className="registered-text text-center mt-3 text-white">
                Already have an account?{" "}
                <Link to={WebRoutes.signin} className="primary-color hover-text">
                    Log In
                </Link>
            </p>

            <OrganizationCodeModal
                open={showOrgCodeModal}
                close={setShowOrgCodeModal}
                onProceed={registerMember}
                loading={loading}
            />
        </>
    );
};
